import EN_STATIC_TRANSLATIONS from '../../../../shared/form-translations.json';

export const FORM_LANGUAGE_COOKIE = 'transport_emery_language';
export const FORM_LANGUAGE_CHANGE_EVENT = 'transport-emery-language-change';
export const FORM_LANGUAGES = ['fr', 'en'];
export const DEFAULT_FORM_LANGUAGE = 'fr';

export const normalizeFormLanguage = (language = '') => {
    const normalizedLanguage = String(language || '').trim().toLowerCase();
    return FORM_LANGUAGES.includes(normalizedLanguage) ? normalizedLanguage : DEFAULT_FORM_LANGUAGE;
};

export const getCookie = (name = '') => {
    if(typeof document === 'undefined' || !name){
        return '';
    }

    const cookie = document.cookie
        .split('; ')
        .find(item => item.startsWith(`${encodeURIComponent(name)}=`));

    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
};

export const setCookie = (name = '', value = '', days = 365) => {
    if(typeof document === 'undefined' || !name){
        return;
    }

    const expires = new Date(Date.now() + (days * 24 * 60 * 60 * 1000)).toUTCString();
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

export const getSavedFormLanguage = () => normalizeFormLanguage(getCookie(FORM_LANGUAGE_COOKIE));

export const saveFormLanguage = (language = DEFAULT_FORM_LANGUAGE) => {
    const normalizedLanguage = normalizeFormLanguage(language);

    setCookie(FORM_LANGUAGE_COOKIE, normalizedLanguage);

    if(typeof window !== 'undefined'){
        window.dispatchEvent(new CustomEvent(FORM_LANGUAGE_CHANGE_EVENT, {
            detail: { language: normalizedLanguage }
        }));
    }

    return normalizedLanguage;
};

const normalizeTranslationKey = (value = '') => {
    return String(value || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[’]/g, "'")
        .replace(/\s+/g, ' ')
        .trim();
};

const EN_MONTHS = {
    Janvier: 'January',
    Février: 'February',
    Mars: 'March',
    Avril: 'April',
    Mai: 'May',
    Juin: 'June',
    Juillet: 'July',
    Août: 'August',
    Septembre: 'September',
    Octobre: 'October',
    Novembre: 'November',
    Décembre: 'December'
};



const EN_TRANSLATION_MAP = new Map(
    Object.entries(EN_STATIC_TRANSLATIONS).map(([key, value]) => [normalizeTranslationKey(key), value])
);

const translateEnglishDateText = (text = '') => {
    const dateMatch = text.match(/^(\d{1,2}) (Janvier|Février|Mars|Avril|Mai|Juin|Juillet|Août|Septembre|Octobre|Novembre|Décembre) (\d{4})$/);

    if(dateMatch){
        return `${EN_MONTHS[dateMatch[2]]} ${dateMatch[1]}, ${dateMatch[3]}`;
    }

    const monthMatch = text.match(/^(Janvier|Février|Mars|Avril|Mai|Juin|Juillet|Août|Septembre|Octobre|Novembre|Décembre) (\d{4})$/);

    if(monthMatch){
        return `${EN_MONTHS[monthMatch[1]]} ${monthMatch[2]}`;
    }

    return text;
};

const translateEnglishDynamicText = (text = '') => {
    const dynamicRules = [
        [/^Étape (\d+) de (\d+)$/i, match => `Step ${match[1]} of ${match[2]}`],
        [/^Aller à l'étape (\d+): (.+)$/i, match => `Go to step ${match[1]}: ${translateInterfaceText(match[2], 'en')}`],
        [/^Supprimer l'option (\d+)$/i, match => `Remove option ${match[1]}`],
        [/^Option (\d+) :$/i, match => `Option ${match[1]}:`],
        [/^Option (\d+)$/i, match => `Option ${match[1]}`],
        [/^Escaliers #(\d+)$/i, match => `Stairs #${match[1]}`],
        [/^Supprimer l'escalier #(\d+)$/i, match => `Remove stair #${match[1]}`],
        [/^Item #(\d+)$/i, match => `Item #${match[1]}`],
        [/^(\d+) sélectionné(?:s)?$/i, match => `${match[1]} selected`],
        [/^(\d+) article\(s\)$/i, match => `${match[1]} item(s)`],
        [/^(\d+) article(?:s)?$/i, match => `${match[1]} item${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) à compléter$/i, match => `${match[1]} to complete`],
        [/^(\d+) info(?:s)? requise(?:s)?$/i, match => `${match[1]} required info`],
        [/^(\d+) particularité(?:s)?$/i, match => `${match[1]} detail${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) déménageur(?:s)?$/i, match => `${match[1]} mover${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) camion(?:s)?$/i, match => `${match[1]} truck${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) chambre(?:s)?$/i, match => `${match[1]} bedroom${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) étage(?:s)?$/i, match => `${match[1]} floor${Number(match[1]) > 1 ? 's' : ''}`],
        [/^Étage (\d+)$/i, match => `Floor ${match[1]}`],
        [/^Sous-sol (\d+)$/i, match => `Basement ${match[1]}`],
        [/^(\d+) escalier(?:s)?$/i, match => `${match[1]} stair${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) volée(?:s)?$/i, match => `${match[1]} flight${Number(match[1]) > 1 ? 's' : ''}`],
        [/^(\d+) escalier(?:s)? • (\d+) volée(?:s)?$/i, match => `${match[1]} stair${Number(match[1]) > 1 ? 's' : ''} • ${match[2]} flight${Number(match[2]) > 1 ? 's' : ''}`],
        [/^(.+) à (.+)$/i, match => `${translateInterfaceText(match[1], 'en')} to ${translateInterfaceText(match[2], 'en')}`]
    ];

    const dateText = translateEnglishDateText(text);

    if(dateText !== text){
        return dateText;
    }

    for(const [pattern, getReplacement] of dynamicRules){
        const match = text.match(pattern);

        if(match){
            return getReplacement(match);
        }
    }

    let translatedText = text;
    const phraseTranslations = [
        ["À compléter", "To complete"],
        ["À choisir", "To choose"],
        ["déménageur(s)", "mover(s)"],
        ["article(s)", "item(s)"],
        ["sélectionnés", "selected"],
        ["sélectionné", "selected"],
        ["à compléter", "to complete"],
        ["Taux horaire", "Hourly rate"],
        ["Heures de travail", "Work hours"],
        ["Supplément carburant", "Fuel surcharge"],
        ["Temps de déplacement", "Travel time"],
        ["Distance totale", "Total distance"],
        ["Base selon les détails de départ", "Base based on pickup details"],
        ["Un article sélectionné demande", "A selected item requires"],
        ["Deux volées d'escaliers ou plus demandent un minimum de", "Two or more flights of stairs require at least"],
        ["Un escalier en colimaçon demande un minimum de", "A spiral staircase requires at least"],
        ["Plus de 50 boîtes demandent un minimum de", "More than 50 boxes require at least"],
        ["Une longue distance de marche demande un minimum de", "A long walking distance requires at least"],
        ["Garage, cour arrière ou entreposage demandent", "Garage, backyard or storage require"],
        ["Le volume saisi dépasse un seuil de grand déménagement, incluant les meubles et électroménagers.", "The entered volume exceeds a large-move threshold, including furniture and appliances."],
        ["Au moins un article est au niveau de poids maximal.", "At least one item is at the maximum weight level."],
        ["Un ou plusieurs articles lourds augmentent l'équipe recommandée.", "One or more heavy items increase the recommended crew."],
        ["Un article plus difficile à transporter augmente l'équipe recommandée.", "A harder-to-move item increases the recommended crew."],
        ["Le pointage de difficulté atteint", "The difficulty score reaches"],
        ["points.", "points."],
        ["nécessitent", "require"],
        ["camions.", "trucks."],
        ["camion.", "truck."],
        ["Le volume ou les contraintes d'accès demandent", "The volume or access constraints require"],
        ["Une révision manuelle a été demandée afin de compléter la soumission sans calcul automatique.", "A manual review was requested to complete the quote without automatic pricing."],
        ["nécessite une révision manuelle.", "requires a manual review."],
        ["contient des informations qui doivent être validées manuellement.", "contains information that must be manually validated."],
        ["Le stationnement n'est pas garanti à l'adresse de départ.", "Parking is not guaranteed at the pickup address."],
        ["Le stationnement n'est pas garanti à l'adresse d'arrivée.", "Parking is not guaranteed at the destination address."],
        ["Un logement situé au 4e étage ou plus sans ascenseur doit être révisé manuellement.", "A unit on the 4th floor or higher without an elevator must be manually reviewed."],
        ["Une très longue distance de marche doit être révisée manuellement.", "A very long walking distance must be manually reviewed."],
        ["Le déménagement semble nécessiter plus de 2 camions.", "The move appears to require more than 2 trucks."],
        ["Les déménagements commerciaux sont révisés manuellement.", "Commercial moves are manually reviewed."]
    ];

    phraseTranslations.forEach(([from, to]) => {
        translatedText = translatedText.split(from).join(to);
    });

    return translatedText;
};

export const translateInterfaceText = (value = '', language = getSavedFormLanguage()) => {
    const normalizedLanguage = normalizeFormLanguage(language);

    if(normalizedLanguage !== 'en'){
        return String(value || '');
    }

    const originalText = String(value || '');
    const normalizedText = normalizeTranslationKey(originalText);

    if(!normalizedText){
        return originalText;
    }

    return EN_TRANSLATION_MAP.get(normalizedText)
        || translateEnglishDynamicText(normalizedText)
        || originalText;
};

const shouldSkipTranslationNode = (node) => {
    const parentElement = node?.parentElement;

    if(!parentElement){
        return true;
    }

    return Boolean(parentElement.closest('script, style, noscript, textarea, input, .language-switcher, [data-no-translate]'));
};

const translateTextNode = (node, language) => {
    if(shouldSkipTranslationNode(node)){
        return;
    }

    const text = node.textContent || '';
    const trimmed = text.trim();

    if(!trimmed){
        return;
    }

    const translated = translateInterfaceText(trimmed, language);

    if(translated === trimmed){
        return;
    }

    const leading = text.match(/^\s*/)?.[0] || '';
    const trailing = text.match(/\s*$/)?.[0] || '';
    node.textContent = `${leading}${translated}${trailing}`;
};

const translateElementAttributes = (element, language) => {
    if(!element || element.closest?.('.language-switcher, [data-no-translate]')){
        return;
    }

    ['placeholder', 'aria-label', 'title', 'alt'].forEach(attribute => {
        if(!element.hasAttribute(attribute)){
            return;
        }

        const value = element.getAttribute(attribute);
        const translated = translateInterfaceText(value, language);

        if(translated !== value){
            element.setAttribute(attribute, translated);
        }
    });

    if(element.tagName === 'INPUT' && ['button', 'submit', 'reset'].includes(String(element.type || '').toLowerCase())){
        const value = element.getAttribute('value');
        const translated = translateInterfaceText(value, language);

        if(value && translated !== value){
            element.setAttribute('value', translated);
        }
    }
};

export const applyFormLanguageTranslations = (root, language = getSavedFormLanguage()) => {
    const normalizedLanguage = normalizeFormLanguage(language);

    if(typeof document === 'undefined' || !root || normalizedLanguage !== 'en'){
        return;
    }

    translateElementAttributes(root, normalizedLanguage);

    const textWalker = document.createTreeWalker(
        root,
        window.NodeFilter ? window.NodeFilter.SHOW_TEXT : 4,
        {
            acceptNode(node){
                return shouldSkipTranslationNode(node)
                    ? (window.NodeFilter ? window.NodeFilter.FILTER_REJECT : 2)
                    : (window.NodeFilter ? window.NodeFilter.FILTER_ACCEPT : 1);
            }
        }
    );
    const textNodes = [];
    let node = textWalker.nextNode();

    while(node){
        textNodes.push(node);
        node = textWalker.nextNode();
    }

    textNodes.forEach(currentNode => translateTextNode(currentNode, normalizedLanguage));
    root.querySelectorAll?.('[placeholder], [aria-label], [title], [alt], input[type="button"], input[type="submit"], input[type="reset"]').forEach(element => {
        translateElementAttributes(element, normalizedLanguage);
    });
};
